declare global {
  interface Window {
    clarity?: (...args: unknown[]) => void
  }
}

/**
 * Custom Microsoft Clarity events, fired via `window.clarity("event", ...)`.
 *
 * These show up alongside Clarity's own auto-detected Smart Events (once
 * Clarity has recorded at least one real occurrence of each), and can be
 * dragged into a Funnel in the Clarity dashboard just like a page visit.
 * Keeping the event names here (rather than as inline strings at each call
 * site) avoids typos/drift between what the code fires and what gets
 * selected when building a funnel.
 */
export const ClarityEvent = {
  /** Fired when login/registration/reset-password routes the user to /verifyInfo. */
  VerificationRequired: 'verificationRequired',
  /** Fired when login/registration/reset-password routes the user straight to /dashboard. */
  VerificationNotRequired: 'verificationNotRequired',
  /** Fired when a workorder is successfully marked verified in the verify-info flow. */
  WorkOrderVerifiedCompleted: 'workOrderVerifiedCompleted',
  WorkOrderUpdateCompleted: 'workOrderUpdateCompleted',
  /**
   * Fired when the user actually attempted to submit verification and it
   * failed (API error, or the workorder id was missing) — distinguishes
   * "tried and hit a real error" from silently never attempting to submit.
   */
  WorkOrderVerifiedFailed: 'workOrderVerifiedFailed',
  WorkOrderUpdateFailed: 'workOrderUpdateFailed',
  SmsPreferenceUpdateFailed: 'smsPreferenceUpdateFailed',
  PackagePreviewUnavailable: 'packagePreviewUnavailable',
  PackageDownloadFailed: 'packageDownloadFailed',
  OptOutSmsFailed: 'optOutSmsFailed',
} as const

export type ClarityEventValue = (typeof ClarityEvent)[keyof typeof ClarityEvent]

export function trackClarityEvent(event: ClarityEventValue) {
  window?.clarity?.('event', event)
}

export function identifyClaritySession(email: string, pageId?: string) {
  window?.clarity?.('identify', email, undefined, pageId, email)
}
